import _ from 'lodash';

import { ILevelProjectData } from 'src/app/infrastructure/models/level-project-data';
import { IProjectMemberForm } from './project-member-form';
import { IProjectData, ProjectData, projectData } from '../../project/models/project-data';
import { IProjectMemberDto, projectMemberDto } from 'src/assets/common/models/project-member-dto.g';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { domainDto } from 'src/assets/common/models/domain-dto.g';
import { ILogosData, LogosData } from '../../shared/logo/logo/models/logos-data';
import { IUserData, UserData } from '../../user/models/user-data';
import { DomainUserData, IDomainUserData } from '../../domain-user/models/domain-user-data';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { dayToLocalDate } from 'src/app/infrastructure/functions/zone-day.function';
import { userDto } from 'src/assets/common/models/user-dto.g';
import { domainUserDisplayName, organisationLabel, userDisplayName } from 'src/app/infrastructure/functions/user-name.function';
import { domainUserDto } from 'src/assets/common/models/domain-user-dto.g';
import { ImageBoxData, imageBoxData } from '../../image/models/image-box-data';
import { imageData } from '../../image/models/image-data';
import { IImageParentData, imageParentData } from '../../image/models/image-parent-data';
import { membershipClosed } from '../../function-member/models/membership-access-dates';

export const projectMemberData = {
    ...imageParentData,
    guid: 'guid',
    nr: 'nr',
    // Een ProjectMember heeft geen eigen naam: dit is de naam van de gekoppelde DomainUser
    // (zelfde rol als domainUserData.name op de DomainUser-pagina).
    name: 'name',
    // name → user_name (firstFilled, zie user-name.function.ts): DomainUser.Name is vaak leeg (privacy-
    // naammodel), dus voor weergave displayName gebruiken — zelfde keten als domainUserData.displayName.
    displayName: 'displayName',
    lastVisit: 'lastVisit',
    isAdmin: 'isAdmin',
    deleted: 'deleted',
    inactive: 'inactive',
    // Toegangsperiode van dit lidmaatschap (zie ProjectMemberDto.StartDate/EndDate backend-side).
    // Kalenderdagen in de zone van het project (backend: StartDay/EndDay; endDate = laatste dag mét
    // toegang) als lokale Date voor weergave en datepicker. startDay/endDay/timeZoneId voor de datumregels.
    startDate: 'startDate',
    endDate: 'endDate',
    startDay: 'startDay',
    endDay: 'endDay',
    timeZoneId: 'timeZoneId',
    // Lidmaatschap beëindigd (eindmoment voorbij, zie membershipClosed): alleen-lezen tot Toegang herstellen.
    closed: 'closed',

    domainUser_guid: 'domainUser_guid',
    domainUser_name: 'domainUser_name',

    domain_name: 'domain_name',

    project_name: 'project_name',
    
    user_guid: 'user_guid',
    user_name: 'user_name',
    user_email: 'user_email',
    // Organisatie van het lid (via zijn DomainUser): 'intern' of de contactnaam; null = onbekend.
    organisation_label: 'organisation_label',

    profilePhoto_thumbGuid: 'domainUser_profilePhoto_thumbGuid',
    domainPhoto_thumbGuid: 'domain_domainPhoto_thumbGuid',
    userPhoto_thumbGuid: 'user_userPhoto_thumbGuid',

    domainUserData: 'domainUserData',
    logosData: 'logosData',
    projectData: 'projectData',
    userData: 'userData',
} as const;

export type TProjectMemberData = (typeof projectMemberData)[keyof typeof projectMemberData];

export interface IProjectMemberData extends ILevelProjectData, IImageParentData {
    [projectMemberData.guid]: string;
    [projectMemberData.nr]: number | null;
    [projectMemberData.lastVisit]: Date | null;
    [projectMemberData.isAdmin]: boolean;
    [projectMemberData.deleted]: boolean;
    [projectMemberData.inactive]: boolean;
    [projectMemberData.name]: string | null;
    [projectMemberData.startDate]: Date | null;
    [projectMemberData.endDate]: Date | null;
    [projectMemberData.startDay]: string | null;
    [projectMemberData.endDay]: string | null;
    [projectMemberData.timeZoneId]: string | null;
    [projectMemberData.closed]: boolean;

    [projectMemberData.domainUser_guid]: string;
    [projectMemberData.domainUser_name]: string;

    [projectMemberData.domain_name]: string;

    [projectMemberData.project_name]: string;

    [projectMemberData.user_guid]: string;
    [projectMemberData.user_name]: string;
    [projectMemberData.user_email]: string;
    [projectMemberData.organisation_label]: string | null;
    [projectMemberData.displayName]: string;

    [projectMemberData.profilePhoto_thumbGuid]?: string | null;
    [projectMemberData.domainPhoto_thumbGuid]?: string | null;
    [projectMemberData.userPhoto_thumbGuid]?: string | null;

    [projectMemberData.domainUserData]: IDomainUserData | null;
    [projectMemberData.logosData]: ILogosData | null;
    [projectMemberData.projectData]: IProjectData | null;
    [projectMemberData.userData]: IUserData | null;
}

export class ProjectMemberData
    implements IProjectMemberData, ILevelProjectData
{
    // IImageParentData: hoofdfoto-banner = profielfoto van de gekoppelde DomainUser (read-only hier,
    // beheer via de DomainUser-pagina) — zelfde bron + User-fallback als DomainUserData.
    [projectMemberData.photoThumbGuid]: string | null = null;
    [projectMemberData.photoOriginGuid]: string | null = null;
    [projectMemberData.photoVersion]: string | null = null;

    [projectMemberData.guid]!: string;
    [projectMemberData.nr]: number | null = null;
    [projectMemberData.lastVisit]: Date | null = null;
    [projectMemberData.isAdmin]!: boolean;
    [projectMemberData.deleted]!: boolean;
    [projectMemberData.inactive]!: boolean;
    [projectMemberData.name]: string | null = null;
    [projectMemberData.startDate]: Date | null = null;
    [projectMemberData.endDate]: Date | null = null;
    [projectMemberData.startDay]: string | null = null;
    [projectMemberData.endDay]: string | null = null;
    [projectMemberData.timeZoneId]: string | null = null;
    [projectMemberData.closed]: boolean = false;

    [projectMemberData.domainUser_guid]!: string;
    [projectMemberData.domainUser_name]!: string;

    [projectMemberData.domain_name]!: string;

    [projectMemberData.project_name]!: string;

    [projectMemberData.user_guid]!: string;
    [projectMemberData.user_name]!: string;
    [projectMemberData.user_email]!: string;
    [projectMemberData.organisation_label]: string | null = null;
    [projectMemberData.displayName]: string = '';

    [projectMemberData.profilePhoto_thumbGuid]?: string | null = null;
    [projectMemberData.domainPhoto_thumbGuid]?: string | null = null;
    [projectMemberData.userPhoto_thumbGuid]?: string | null = null;

    [projectMemberData.domainUserData]: IDomainUserData | null = null;
    [projectMemberData.logosData]: ILogosData | null = null;
    [projectMemberData.projectData]: IProjectData | null = null;
    [projectMemberData.userData]: IUserData | null = null;

    protected constructor(modelDto?: IProjectMemberDto | null);
    protected constructor(modelData: IProjectMemberData, modelForm: IProjectMemberForm);
    protected constructor(value?: IProjectMemberDto | IProjectMemberData | null, modelForm?: IProjectMemberForm) {
        if (!!modelForm) {
            const _modelData = value as IProjectMemberData;
            _.assign(this, _modelData, modelForm);
            this[projectMemberData.displayName] = domainUserDisplayName(this[projectMemberData.name], this[projectMemberData.user_name]);
        } else if (!!value) {
            const __modelDto = value as IProjectMemberDto;
            const __lastVisit = __modelDto[projectMemberDto.lastVisit];

            this[projectMemberData.guid] = __modelDto[projectMemberDto.guid]!;
            this[projectMemberData.nr] = __modelDto[projectMemberDto.nr];
            this[projectMemberData.lastVisit] = !!__lastVisit ? new Date(__lastVisit) : null;
            this[projectMemberData.isAdmin] = __modelDto[projectMemberDto.isAdmin] ?? false;
            this[projectMemberData.deleted] = __modelDto[projectMemberDto.deleted]!;
            this[projectMemberData.inactive] = __modelDto[projectMemberDto.inactive]!;
            this[projectMemberData.name] = __modelDto[projectMemberDto.domainUserDto]?.[domainUserDto.name] ?? null;
            this[projectMemberData.startDay] = __modelDto[projectMemberDto.startDay] ?? null;
            this[projectMemberData.endDay] = __modelDto[projectMemberDto.endDay] ?? null;
            this[projectMemberData.timeZoneId] = __modelDto[projectMemberDto.timeZoneId] ?? null;
            this[projectMemberData.startDate] = dayToLocalDate(this[projectMemberData.startDay]);
            this[projectMemberData.endDate] = dayToLocalDate(this[projectMemberData.endDay]);
            this[projectMemberData.closed] = membershipClosed(__modelDto[projectMemberDto.endDate]);

            this[projectMemberData.user_guid] = __modelDto[projectMemberDto.userDto]?.[userDto.guid]!;
            this[projectMemberData.user_name] = userDisplayName(__modelDto[projectMemberDto.userDto]);
            this[projectMemberData.user_email] = __modelDto[projectMemberDto.userDto]?.[userDto.email]!;
            this[projectMemberData.organisation_label] = organisationLabel(
                __modelDto[projectMemberDto.domainUserDto]?.[domainUserDto.affiliation], __modelDto[projectMemberDto.domainUserDto]?.[domainUserDto.organisation_Name]);
            this[projectMemberData.displayName] = domainUserDisplayName(this[projectMemberData.name], this[projectMemberData.user_name]);

            this[projectMemberData.profilePhoto_thumbGuid] = __modelDto[projectMemberDto.profilePhotoDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
            this[projectMemberData.domainPhoto_thumbGuid] = __modelDto[projectMemberDto.domainPhotoDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
            this[projectMemberData.userPhoto_thumbGuid] = __modelDto[projectMemberDto.userPhotoDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;

            const __profilePhoto = ImageBoxData.create(__modelDto[projectMemberDto.profilePhotoDto] ?? __modelDto[projectMemberDto.userPhotoDto]);
            this[projectMemberData.photoThumbGuid] = __profilePhoto?.[imageBoxData.photoThumbData]?.[imageData.guid] ?? null;
            this[projectMemberData.photoOriginGuid] = __profilePhoto?.[imageBoxData.photoData]?.[imageData.guid] ?? null;
            this[projectMemberData.photoVersion] = __profilePhoto?.[imageBoxData.photoThumbData]?.[imageData.modifiedOn] ?? null;

            this[projectMemberData.logosData] = LogosData.create(__modelDto[projectMemberDto.domainDto]?.[domainDto.logosDto]);
            this[projectMemberData.domainUserData] = DomainUserData.create(__modelDto[projectMemberDto.domainUserDto]);
            this[projectMemberData.projectData] = ProjectData.create(__modelDto[projectMemberDto.projectDto]);
            this[projectMemberData.project_name] = this[projectMemberData.projectData]?.[projectData.name]!;
            this[projectMemberData.userData] = UserData.create(__modelDto[projectMemberDto.userDto]);
        }
    }
        
    static create(modelDto?: IProjectMemberDto | null): IProjectMemberData | null
    static create(modelData: IProjectMemberData, modelForm: IProjectMemberForm): IProjectMemberData | null
    static create(value?: IProjectMemberDto | IProjectMemberData | null, modelForm?: IProjectMemberForm): IProjectMemberData | null {

        if (!!modelForm) {
            const _modelData = value as IProjectMemberData;
            return new ProjectMemberData(_modelData, modelForm);
        } else {
            const _modelDto = value as IProjectMemberDto;
            return _modelDto ? new ProjectMemberData(_modelDto) : null;
        }
    }
}
